import type { QueryClient } from '@tanstack/react-query';
import { currentLocale } from '@shared/utils/dateFormatter';
import { unstable_batchedUpdates } from 'react-dom';
import { z } from 'zod';
import { showWebSocketNotification } from '@shared/utils/websocketNotificationApi';

import type { Device, DeviceInterface } from '@features/devices/types';
import type {
  PollerFdbEntry,
  PollerIfacesCacheResponse,
  PollerInterfacePayload,
  PollerTempliteSnapshot,
  PollerVlanCacheResponse,
  PollerVlanEntry,
} from '@features/devices/api';
import {
  useDeviceMacSearchStore,
  type MacEventAction,
  type MacTimelineEvent,
} from '@features/devices/store/deviceMacSearchStore';
import {
  useDeviceInterfaceHistoryStore,
  type InterfaceHistorySource,
} from '@features/devices/store/deviceInterfaceHistoryStore';
import { useDevicePonTasksStore, type PonTaskStatus, type PonTaskCategory } from '@features/devices/store/devicePonTasksStore';
import { useDeviceFdbRealtimeStore, buildFdbEntryKey } from '@features/devices/store/deviceFdbRealtimeStore';
import { useDeviceCableTestStore, type CableTestActionStatus } from '@features/devices/store/deviceCableTestStore';
import { formatMac } from '@shared/utils/mac';
import type { WebSocketEventRouter } from '@/shared/websocket/router';
import type { WebSocketEnvelope } from '@/shared/websocket/types';

const DeviceStatusSchema = z.object({
  status: z.string(),
  device_id: z.union([z.number(), z.string()]),
});

const DeviceInterfacesSchema = z.object({
  device_id: z.union([z.number(), z.string()]),
  generated_at: z.string().optional(),
  interfaces: z.array(z.record(z.string(), z.unknown())).optional(),
  templite: z.record(z.string(), z.unknown()).optional(),
  task: z.string().optional(),
  task_id: z.union([z.string(), z.number()]).optional(),
});

const DeviceVlanSchema = z.object({
  device_id: z.union([z.number(), z.string()]),
  vlans: z.array(z.record(z.string(), z.unknown())).optional(),
  generated_at: z.string().optional(),
  task: z.string().optional(),
  task_id: z.string().optional(),
});

const DeviceFdbSchema = z.object({
  device_id: z.union([z.number(), z.string()]),
  entries: z.array(z.record(z.string(), z.unknown())).optional(),
  generated_at: z.string().optional(),
  task: z.string().optional(),
  task_id: z.string().optional(),
});

const DeviceIdOnlySchema = z.object({
  device_id: z.union([z.number(), z.string()]),
});

// Схема для батч-обновлений ONU
const PonOnuBatchUpdateSchema = z.object({
  device_id: z.union([z.number(), z.string()]),
  onu_ids: z.array(z.number()).optional(), // Опционально: если пустой - обновить все ONU
  count: z.number().optional(),
  timestamp: z.number().optional(),
});

const PonTaskSchema = z.object({
  device_id: z.union([z.number(), z.string()]),
  status: z.string().optional(),
  message: z.string().optional(),
  task_name: z.string().optional(),
  task_id: z.union([z.string(), z.number()]).optional(),
  priority: z.number().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  employee_name: z.string().optional(),
  onus: z.array(z.unknown()).optional(),
  onu_identifiers: z.array(z.unknown()).optional(),
});

const MacEventSchema = z.object({
  mac_address: z.string().optional(),
  action: z.string().optional(),
  port_name: z.string().optional(),
  port_number: z.union([z.number(), z.string()]).optional(),
  vlan_id: z.union([z.number(), z.string(), z.null()]).optional(),
  device_id: z.union([z.number(), z.string()]).optional(),
  device_name: z.string().optional(),
  source: z.string().optional(),
  timestamp: z.string().optional(),
  event_id: z.union([z.string(), z.number()]).optional(),
});

const CableTestSchema = z.object({
  device_id: z.union([z.number(), z.string()]),
  status: z.string().optional(),
  is_finish: z.boolean().optional(),
  message: z.string().optional(),
  status_message: z.string().optional(),
  status_text: z.string().optional(),
  finished_at: z.string().optional(),
  updated_at: z.string().optional(),
});

// Схема для событий модуля (module.started, module.finished)
const ModuleEventSchema = z.object({
  module_name: z.string(),
  schedule_id: z.union([z.number(), z.string()]).optional(),
  history_id: z.union([z.number(), z.string()]).optional(),
  status: z.string().optional(), // для module.finished: "success" | "error"
  error_message: z.string().optional(), // для module.finished с ошибкой
  execution_time_ms: z.number().optional(), // для module.finished
  started_at: z.union([z.number(), z.string()]).optional(), // для module.started
  finished_at: z.union([z.number(), z.string()]).optional(), // для module.finished
});

type DeviceEventsDependencies = {
  queryClient: QueryClient;
  translate: (key: string, params?: Record<string, unknown>) => string;
};

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  if (typeof value === 'bigint') {
    return Number(value);
  }
  return fallback;
};

const pickString = (raw: Record<string, unknown>, keys: string[], fallback: string): string => {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return fallback;
};

const normalizeVlanEntry = (raw: unknown): PollerVlanEntry | null => {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const vlanIdCandidate = record.vlan_id ?? record.vlanId ?? record.id;
  const vlanId =
    typeof vlanIdCandidate === 'number'
      ? vlanIdCandidate
      : typeof vlanIdCandidate === 'string'
        ? Number(vlanIdCandidate.trim())
        : typeof vlanIdCandidate === 'bigint'
          ? Number(vlanIdCandidate)
          : Number.NaN;

  if (!Number.isFinite(vlanId)) {
    return null;
  }

  const entry: PollerVlanEntry = {
    vlan_id: Number(vlanId),
  };

  if (typeof record.name === 'string') {
    const trimmed = record.name.trim();
    if (trimmed.length > 0) {
      entry.name = trimmed;
    }
  }

  const statusCandidate = record.status ?? record.state;
  if (typeof statusCandidate === 'string') {
    const normalized = statusCandidate.trim();
    if (normalized.length > 0) {
      entry.status = normalized.toLowerCase();
    }
  }

  const portsCandidate = record.ports ?? record.port_list ?? record.members;
  if (Array.isArray(portsCandidate)) {
    const ports = portsCandidate
      .map((value) => {
        if (typeof value === 'number' && Number.isFinite(value)) {
          return value;
        }
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed.length === 0) {
            return null;
          }
          const parsed = Number(trimmed);
          return Number.isFinite(parsed) ? parsed : null;
        }
        if (typeof value === 'bigint') {
          return Number(value);
        }
        return null;
      })
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    if (ports.length > 0) {
      entry.ports = ports;
    }
  }

  return entry;
};

const normalizeFdbEntry = (raw: unknown): PollerFdbEntry | null => {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const macRaw = pickString(record, ['mac_address', 'macAddress', 'mac'], '').toLowerCase();
  if (!macRaw) {
    return null;
  }

  const vlanCandidate = record.vlan_id ?? record.vlanId ?? record.vid ?? record.vlan;
  const portCandidate = record.port ?? record.iface ?? record.if_index ?? record.ifIndex;

  const entry: PollerFdbEntry = {
    mac_address: macRaw,
  };

  const vlan = toNumber(vlanCandidate, Number.NaN);
  if (typeof vlan === 'number' && Number.isFinite(vlan)) {
    entry.vlan_id = vlan;
  }

  const port = toNumber(portCandidate, Number.NaN);
  if (typeof port === 'number' && Number.isFinite(port)) {
    entry.port = port;
  }

  const portName = pickString(record, ['port_name', 'portName', 'iface_caption'], '');
  if (portName) {
    entry.port_name = portName;
  }

  const typeValue = pickString(record, ['type', 'entry_type', 'entryType'], '');
  if (typeValue) {
    entry.type = typeValue.toLowerCase();
  }

  const dateFirst = toNumber(
    record.date_first ?? record.first_seen ?? record.firstSeen,
    Number.NaN,
  );
  if (typeof dateFirst === 'number' && Number.isFinite(dateFirst)) {
    entry.date_first = dateFirst;
  }

  const dateLast = toNumber(record.date_last ?? record.last_seen ?? record.lastSeen, Number.NaN);
  if (typeof dateLast === 'number' && Number.isFinite(dateLast)) {
    entry.date_last = dateLast;
  }

  return entry;
};

const toIsoString = (value?: string | number): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 1_000_000_000_000 ? value : value * 1000;
    return new Date(ms).toISOString();
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      const ms = numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
      return new Date(ms).toISOString();
    }
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }
  return '';
};

const resolvePortName = (portName?: string, port?: number): string => {
  const trimmed = typeof portName === 'string' ? portName.trim() : '';
  if (trimmed.length > 0) {
    return trimmed;
  }
  if (typeof port === 'number' && Number.isFinite(port) && port > 0) {
    return `Port ${port}`;
  }
  return '';
};

const resolvePonTimelineCategory = (taskName?: string): PonTaskCategory => {
  if (!taskName) {
    return 'diagnostics';
  }
  const normalized = taskName.toLowerCase();
  if (normalized.includes('profile') || normalized.includes('reboot')) {
    return 'maintenance';
  }
  if (normalized.includes('inventory')) {
    return 'inventory';
  }
  if (normalized.includes('system')) {
    return 'system';
  }
  return 'diagnostics';
};

const normalizeCableTestActionStatus = (
  rawStatus?: string,
  isFinish?: boolean,
): CableTestActionStatus | null => {
  if (typeof rawStatus === 'string') {
    const normalized = rawStatus.trim().toLowerCase();
    if (['running', 'in_progress', 'processing', 'queued', 'starting'].includes(normalized)) {
      return 'pending';
    }
    if (['finished', 'completed', 'success', 'done', 'finished_success'].includes(normalized)) {
      return 'success';
    }
    if (['failed', 'error', 'cancelled', 'timeout'].includes(normalized)) {
      return 'error';
    }
  }
  if (isFinish === true) {
    return 'success';
  }
  return null;
};

const buildCableTestStatusMessage = (
  status: CableTestActionStatus,
  payload: Record<string, unknown>,
  translate: (key: string, params?: Record<string, unknown>) => string,
): string => {
  const rawMessage =
    typeof payload.message === 'string'
      ? payload.message
      : typeof payload.status_message === 'string'
      ? payload.status_message
      : typeof payload.status_text === 'string'
      ? payload.status_text
      : undefined;

  if (status === 'pending') {
    return rawMessage ?? translate('devices:cableTest.status.pending');
  }

  if (status === 'success') {
    if (rawMessage) {
      return rawMessage;
    }
    const finishedAt =
      (typeof payload.finished_at === 'string' && payload.finished_at)
      || (typeof payload.updated_at === 'string' && payload.updated_at)
      || new Date().toLocaleTimeString(currentLocale());
    return translate('devices:cableTest.status.success', { time: finishedAt });
  }

  const errorText =
    rawMessage
    ?? (typeof payload.error === 'string' ? payload.error : undefined)
    ?? translate('devices:cableTest.testFailed');
  return translate('devices:cableTest.status.error', { message: errorText });
};

const hasInterfaceChanged = (previous: DeviceInterface | undefined, next: DeviceInterface): boolean => {
  if (!previous) {
    return true;
  }

  return (
    previous.admin_status !== next.admin_status
    || previous.oper_status !== next.oper_status
    || previous.in_octets !== next.in_octets
    || previous.out_octets !== next.out_octets
    || previous.in_errors !== next.in_errors
    || previous.out_errors !== next.out_errors
    || previous.in_discards !== next.in_discards
    || previous.out_discards !== next.out_discards
    || previous.speed !== next.speed
    || previous.mtu !== next.mtu
    || previous.last_poll !== next.last_poll
    || previous.description !== next.description
  );
};

const normalizeInterfaceRecord = (
  raw: Record<string, unknown>,
  deviceId: number,
  previous?: DeviceInterface,
): DeviceInterface | null => {
  const indexRaw = raw.if_index ?? raw.ifIndex ?? raw.index ?? raw.ifId;
  const index =
    typeof indexRaw === 'number'
      ? indexRaw
      : typeof indexRaw === 'string'
        ? Number(indexRaw)
        : Number.NaN;
  if (!Number.isFinite(index)) {
    return null;
  }

  return {
    device_id: previous?.device_id ?? deviceId,
    if_index: index,
    name: pickString(raw, ['name', 'if_name', 'ifName'], previous?.name ?? `if${index}`),
    description: pickString(
      raw,
      ['description', 'if_descr', 'ifDescription'],
      previous?.description ?? '',
    ),
    admin_status: pickString(
      raw,
      ['admin_status', 'adminStatus', 'ifAdminStatus'],
      previous?.admin_status ?? 'unknown',
    ),
    oper_status: pickString(
      raw,
      ['oper_status', 'operStatus', 'ifOperStatus', 'status'],
      previous?.oper_status ?? 'unknown',
    ),
    speed: toNumber(raw.speed ?? raw.if_speed ?? raw.ifSpeed, previous?.speed ?? 0),
    mtu: toNumber(raw.mtu ?? raw.if_mtu ?? raw.ifMtu, previous?.mtu ?? 0),
    type: toNumber(raw.type ?? raw.if_type ?? raw.ifType, previous?.type ?? 0),
    in_octets: toNumber(raw.in_octets ?? raw.inOctets, previous?.in_octets ?? 0),
    out_octets: toNumber(raw.out_octets ?? raw.outOctets, previous?.out_octets ?? 0),
    in_errors: toNumber(raw.in_errors ?? raw.inErrors, previous?.in_errors ?? 0),
    out_errors: toNumber(raw.out_errors ?? raw.outErrors, previous?.out_errors ?? 0),
    in_discards: toNumber(raw.in_discards ?? raw.inDiscards, previous?.in_discards ?? 0),
    out_discards: toNumber(raw.out_discards ?? raw.outDiscards, previous?.out_discards ?? 0),
    phys_address: pickString(
      raw,
      ['phys_address', 'physAddress', 'mac', 'mac_address', 'macAddress'],
      previous?.phys_address ?? '',
    ),
    last_poll: pickString(
      raw,
      ['last_poll', 'lastPoll', 'polled_at', 'polledAt', 'updated_at', 'updatedAt'],
      previous?.last_poll ?? '',
    ),
  };
};

const mergeInterfaceData = (
  queryClient: QueryClient,
  deviceId: number,
  records: Array<Record<string, unknown>>,
  generatedAt?: string,
  source: InterfaceHistorySource = 'realtime',
) => {
  if (!Array.isArray(records) || records.length === 0) {
    return;
  }

  const changedIndexes: number[] = [];

  queryClient.setQueryData<DeviceInterface[]>(
    ['device', deviceId, 'interfaces'],
    (previous) => {
      const collection = new Map<number, DeviceInterface>();
      (previous ?? []).forEach((item) => {
        collection.set(item.if_index, item);
      });

      records.forEach((rawRecord) => {
        const indexRaw = rawRecord.if_index ?? rawRecord.ifIndex ?? rawRecord.index ?? rawRecord.ifId;
        const index =
          typeof indexRaw === 'number'
            ? indexRaw
            : typeof indexRaw === 'string'
              ? Number(indexRaw)
              : Number.NaN;
        if (!Number.isFinite(index)) {
          return;
        }

        const previousRecord = collection.get(index);
        const normalized = normalizeInterfaceRecord(rawRecord, deviceId, previousRecord);
        if (!normalized) {
          return;
        }

        collection.set(index, normalized);
        if (hasInterfaceChanged(previousRecord, normalized)) {
          changedIndexes.push(index);
        }
      });

      if (collection.size === 0) {
        return previous ?? [];
      }

      return Array.from(collection.values()).sort((a, b) => a.if_index - b.if_index);
    },
  );

  if (changedIndexes.length > 0) {
    const timestamp = generatedAt ?? new Date().toISOString();
    useDeviceInterfaceHistoryStore
      .getState()
      .registerRealtimeChange(deviceId, timestamp, changedIndexes, source);
  }
};

const updatePollerInterfacesCache = (
  queryClient: QueryClient,
  deviceId: number,
  payload: z.infer<typeof DeviceInterfacesSchema>,
) => {
  const hasInterfaces = Array.isArray(payload.interfaces) && payload.interfaces.length > 0;
  const hasTemplite = payload.templite && typeof payload.templite === 'object';
  const hasTimestamp = typeof payload.generated_at === 'string' && payload.generated_at.length > 0;

  if (!hasInterfaces && !hasTemplite && !hasTimestamp) {
    return;
  }

  queryClient.setQueryData<PollerIfacesCacheResponse | undefined>(
    ['devices', deviceId, 'poller', 'ifaces'],
    (previous: PollerIfacesCacheResponse | undefined) => {
      const prevPayload = previous?.data.payload ?? {};
      const nextPayload = {
        ...prevPayload,
      };

      if (hasInterfaces) {
        nextPayload.interfaces = payload.interfaces as unknown as PollerInterfacePayload[];
      }

      if (hasTemplite) {
        nextPayload.templite = payload.templite as PollerTempliteSnapshot;
      }

      if (!nextPayload.interfaces && !nextPayload.templite) {
        return previous;
      }

      const taskSource = payload.task ?? payload.task_id ?? previous?.data.task ?? 'IfList';
      const task = typeof taskSource === 'string' ? taskSource : String(taskSource);

      return {
        data: {
          task,
          payload: nextPayload,
        },
        meta: {
          generatedAt:
            payload.generated_at
            ?? previous?.meta.generatedAt
            ?? new Date().toISOString(),
        },
      };
    },
  );
};

const applyPonTaskUpdate = (
  queryClient: QueryClient,
  payload: z.infer<typeof PonTaskSchema>,
  targetDeviceId?: number,
) => {
  const deviceId = Number(payload.device_id);
  if (!Number.isFinite(deviceId)) {
    if (typeof targetDeviceId === 'number') {
      scheduleInvalidate(queryClient, ['device', targetDeviceId, 'onu']);
    } else {
      scheduleInvalidate(queryClient, ['device', 'any', 'onu']);
    }
    return;
  }

  if (typeof targetDeviceId === 'number' && deviceId !== targetDeviceId) {
    return;
  }

  scheduleInvalidate(queryClient, ['device', deviceId, 'onu']);

  const statusMap: Record<string, PonTaskStatus> = {
    scheduled: 'scheduled',
    scheduling: 'scheduled',
    queued: 'queued',
    queue: 'queued',
    running: 'running',
    processing: 'running',
    success: 'completed',
    completed: 'completed',
    done: 'completed',
    failed: 'failed',
    error: 'failed',
    warning: 'warning',
  };

  const rawStatus = typeof payload.status === 'string' ? payload.status.toLowerCase() : '';
  const status: PonTaskStatus = statusMap[rawStatus] ?? 'unknown';
  const addPonTask = useDevicePonTasksStore.getState().add;

  const candidateList = payload.onus ?? payload.onu_identifiers;
  const normalizedOnus = Array.isArray(candidateList)
    ? candidateList
        .map((value) => (typeof value === 'string' ? value : value != null ? String(value) : ''))
        .filter((value) => value.length > 0)
    : undefined;

  addPonTask({
    id: String(
      payload.task_id ?? `${deviceId}-${payload.task_name ?? 'pon'}-${payload.created_at ?? Date.now()}`,
    ),
    deviceId,
    taskName: payload.task_name ?? 'unknown',
    status,
    message: payload.message ?? null,
    priority: typeof payload.priority === 'number' ? payload.priority : null,
    author: payload.employee_name ?? null,
    onus: normalizedOnus,
    createdAt: payload.created_at ?? new Date().toISOString(),
    updatedAt: payload.updated_at,
    source: 'ws',
    origin: 'poller',
    category: resolvePonTimelineCategory(payload.task_name ?? undefined),
  });

  if (payload.message) {
    showWebSocketNotification('info', payload.message);
  }
};

// Общий дебаунсер инвалидаций, чтобы не спамить React Query при шквале событий
const invalidateBuffer = new Set<string>();
let invalidateTimer: ReturnType<typeof setTimeout> | null = null;
const scheduleInvalidate = (queryClient: QueryClient, key: Array<string | number>, delay = 150) => {
  const k = JSON.stringify(key);
  invalidateBuffer.add(k);
  // В тестовой среде — выполняем немедленно, чтобы unit‑тесты видели вызов
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test') {
    const keys = Array.from(invalidateBuffer).map((v) => JSON.parse(v));
    invalidateBuffer.clear();
    (queryClient as any).batch?.(() => {
      keys.forEach((qk) => queryClient.invalidateQueries({ queryKey: qk }));
    }) ??
      keys.forEach((qk) => queryClient.invalidateQueries({ queryKey: qk }));
    return;
  }
  if (invalidateTimer) return;
  invalidateTimer = setTimeout(() => {
    const keys = Array.from(invalidateBuffer).map((v) => JSON.parse(v));
    invalidateBuffer.clear();
    invalidateTimer = null;
    // batch, если доступен (React Query v5)
    (queryClient as any).batch?.(() => {
      keys.forEach((qk) => queryClient.invalidateQueries({ queryKey: qk }));
    }) ??
      keys.forEach((qk) => queryClient.invalidateQueries({ queryKey: qk }));
  }, delay);
};

const processMacRealtimeEvent = (
  queryClient: QueryClient,
  payload: z.infer<typeof MacEventSchema>,
) => {
  if (!payload.mac_address) {
    return;
  }

  const normalizedMac = formatMac(payload.mac_address);
  if (normalizedMac.length !== 17) {
    return;
  }

  const macStore = useDeviceMacSearchStore.getState();
  const registerFdbEvent = useDeviceFdbRealtimeStore.getState().registerEvent;

  const timestamp = payload.timestamp ?? new Date().toISOString();
  const eventId = payload.event_id != null ? String(payload.event_id) : `${normalizedMac}-${timestamp}`;
  const rawAction = (payload.action ?? 'learned').toLowerCase();
  const allowedActions: MacEventAction[] = ['learned', 'removed', 'moved'];
  const safeAction = allowedActions.includes(rawAction as MacEventAction)
    ? (rawAction as MacEventAction)
    : 'learned';
  const portNumber = payload.port_number != null ? Number(payload.port_number) : undefined;
  const vlanId =
    payload.vlan_id != null && payload.vlan_id !== '' ? Number(payload.vlan_id) : null;
  const deviceId = payload.device_id != null && payload.device_id !== '' ? Number(payload.device_id) : undefined;

  const macEvent: MacTimelineEvent = {
    id: eventId,
    macAddress: normalizedMac,
    action: safeAction,
    deviceId,
    deviceName: payload.device_name,
    portName: payload.port_name,
    portNumber,
    vlanId,
    source: payload.source ?? 'realtime',
    timestamp,
  };

  macStore.recordRealtimeEvent(macEvent);
  macStore.pushEvent(macEvent);

  if (deviceId != null && Number.isFinite(deviceId)) {
    const highlightKey = buildFdbEntryKey(normalizedMac, portNumber, vlanId);
    registerFdbEvent(deviceId, highlightKey, timestamp);
    scheduleInvalidate(queryClient, ['device', deviceId, 'fdb']);
  } else {
    // Широкая инвалидация FDB по всем устройствам редуцирована до батч‑шедулера
    scheduleInvalidate(queryClient, ['device', 'any', 'fdb']);
  }

  scheduleInvalidate(queryClient, ['devices', 'mac', normalizedMac]);
};
const normalizeDeviceId = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

type DeviceRealtimeChannels = {
  interfaces?: boolean;
  monitoring?: boolean;
  cable?: boolean;
  pon?: boolean;
};

interface DeviceScopedDependencies extends DeviceEventsDependencies {
  deviceId: number;
  channels?: DeviceRealtimeChannels;
}

/**
 * Данные одного устройства: `['devices', <id>, '<раздел>', …]` — id числом, затем строковый
 * сегмент (`'mac-history'`, `'interfaces'`, `'poller'`, `'snmp-history'`).
 *
 * ⚠️ Одного «число вторым элементом» мало: список по разделу (`DeviceListByType`) держит там
 * id ТИПА раздела, а третьим — id раздела или `undefined`, но не строку. Такой ключ — список,
 * статусы его касаются.
 */
export const isSingleDeviceQueryKey = (queryKey: readonly unknown[]): boolean =>
  queryKey[0] === 'devices' && typeof queryKey[1] === 'number' && typeof queryKey[2] === 'string';

/** Окно сведения пачки `device_status` в одну инвалидацию списков. */
export const DEVICE_STATUS_LIST_INVALIDATION_DELAY_MS = 500;

export const registerDeviceGlobalWebSocketHandlers = (
  router: WebSocketEventRouter,
  { queryClient, translate }: DeviceEventsDependencies,
): () => void => {
  const disposers: Array<() => void> = [];

  // ⚠️⚠️ Статус устройства меняет СПИСКИ и отчёты, но не данные карточки. Прежде здесь стоял
  // `invalidateQueries({ queryKey: ['devices'] })` на КАЖДОЕ событие — это префикс, и под него
  // попадали запросы открытой карточки (`['devices', id, 'mac-history', 'summary']`,
  // `['devices', id, 'interfaces', 'vlans']`, `['devices', id, 'poller', …]`). `wnm_checker`
  // раз в минуту шлёт статусы по всему парку пачкой: замер стенда 2026-09-28, карточка 600 —
  // 71 повтор каждого из двух запросов за 0,7 с на границе минуты.
  //
  // Две правки: (1) данные ОДНОГО устройства (`isSingleDeviceQueryKey`) не трогаем;
  // (2) пачка событий сводится в одну инвалидацию на такт.
  let pendingListInvalidation: ReturnType<typeof setTimeout> | null = null;
  const invalidateDeviceLists = () => {
    if (pendingListInvalidation !== null) {
      return;
    }
    pendingListInvalidation = setTimeout(() => {
      pendingListInvalidation = null;
      queryClient.invalidateQueries({
        queryKey: ['devices'],
        predicate: (query) => !isSingleDeviceQueryKey(query.queryKey),
      });
    }, DEVICE_STATUS_LIST_INVALIDATION_DELAY_MS);
  };
  disposers.push(() => {
    if (pendingListInvalidation !== null) {
      clearTimeout(pendingListInvalidation);
      pendingListInvalidation = null;
    }
  });

  disposers.push(
    router.on('device_status', (event: WebSocketEnvelope) => {
      const result = DeviceStatusSchema.safeParse(event.data ?? {});

      invalidateDeviceLists();
      
      if (!result.success) {
        return;
      }

      const { status, device_id } = result.data;
      const id = Number(device_id);
      if (!Number.isFinite(id)) {
        return;
      }

      // Обновляем конкретное устройство, если оно загружено
      queryClient.setQueryData<Device | undefined>(['device', id], (prev) => {
        if (!prev) {
          return prev;
        }
        
        const isOnline = status.toLowerCase() === 'online';
        return {
          ...prev,
          is_online: isOnline,
        };
      });

      // Показываем уведомление только при переходе в offline
      if (status.toLowerCase() === 'offline') {
        const messageText = translate('devices.notifications.deviceOffline', { id });
        if (messageText) {
          showWebSocketNotification('error', messageText, 'Устройство недоступно');
        }
      }
    }),
  );

  const macHandler = (event: WebSocketEnvelope) => {
    const parsed = MacEventSchema.safeParse(event.data ?? {});
    if (!parsed.success) {
      return;
    }
    processMacRealtimeEvent(queryClient, parsed.data);
  };

  disposers.push(router.on('device_mac_update', macHandler));
  disposers.push(router.on('device_mac_activity', macHandler));

  return () => {
    disposers.forEach((dispose) => dispose());
  };
};

export const registerDeviceScopedWebSocketHandlers = (
  router: WebSocketEventRouter,
  { queryClient, translate, deviceId, channels }: DeviceScopedDependencies,
): () => void => {
  const disposers: Array<() => void> = [];
  const channelFlags: Required<DeviceRealtimeChannels> = {
    interfaces: channels?.interfaces ?? true,
    monitoring: channels?.monitoring ?? true,
    cable: channels?.cable ?? true,
    pon: channels?.pon ?? true,
  };

  // Локальные буферы и дебаунсеры для сглаживания частых событий
  const fdbBuffer = new Map<number, { entries: unknown[]; generatedAt?: string }>();
  const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
// Optional Web Worker для нормализации крупных payload (включается флагом)
const WS_WORKER_ENABLED = import.meta.env.VITE_ENABLE_WS_WORKER !== 'false';
let realtimeWorker: Worker | null = null;
function getRealtimeWorker(): Worker | null {
  if (!WS_WORKER_ENABLED) return null;
  if (typeof window === 'undefined') return null;
  try {
    if (!realtimeWorker) {
      realtimeWorker = new Worker(new URL('@/workers/realtimeNormalizer.ts', import.meta.url), {
        type: 'module',
      });
      realtimeWorker.onmessage = () => {};
      realtimeWorker.onerror = () => {};
    }
    return realtimeWorker;
  } catch {
    return null;
  }
}

  const scheduleFlush = (key: string, fn: () => void, delay = 50) => {
    // В тестовой среде выполняем сразу, чтобы не ломать unit-тесты
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test') {
      unstable_batchedUpdates(fn);
      return;
    }
    const prev = debounceTimers.get(key);
    if (prev) clearTimeout(prev);
    const timer = setTimeout(() => {
      debounceTimers.delete(key);
      unstable_batchedUpdates(fn);
    }, delay);
    debounceTimers.set(key, timer);
  };
  const invalidateInterfaces = () => scheduleInvalidate(queryClient, ['device', deviceId, 'interfaces']);

  const invalidateCable = () => scheduleInvalidate(queryClient, ['device', deviceId, 'cable-test']);

  const matchesDevice = (value: unknown) => {
    const normalized = normalizeDeviceId(value);
    return normalized !== null && normalized === deviceId;
  };

  if (channelFlags.cable) {
    const cableHandler = (event: WebSocketEnvelope) => {
      const parsed = CableTestSchema.safeParse(event.data ?? {});
      if (!parsed.success) {
        return;
      }

      const payload = parsed.data;
      if (!matchesDevice(payload.device_id)) {
        return;
      }

      invalidateCable();

      const status = normalizeCableTestActionStatus(payload.status, payload.is_finish ?? undefined);
      if (!status) {
        return;
      }

      const messageText = buildCableTestStatusMessage(
        status,
        payload as Record<string, unknown>,
        translate,
      );
      useDeviceCableTestStore.getState().pushActionStatus(deviceId, {
        action: 'history',
        status,
        message: messageText,
        timestamp: new Date().toISOString(),
      });
    };

    disposers.push(router.on('device_cable_test_update', cableHandler));
    disposers.push(router.on('device_cabletest_update', cableHandler));
  }

  const interfaceHandler = (event: WebSocketEnvelope, source: InterfaceHistorySource) => {
    const parsed = DeviceInterfacesSchema.safeParse(event.data ?? {});
    if (!parsed.success) {
      invalidateInterfaces();
      return;
    }

    const payload = parsed.data;
    if (!matchesDevice(payload.device_id)) {
      return;
    }

    if (Array.isArray(payload.interfaces) && payload.interfaces.length > 0) {
      mergeInterfaceData(
        queryClient,
        deviceId,
        payload.interfaces as Array<Record<string, unknown>>,
        payload.generated_at,
        source,
      );
    }

    updatePollerInterfacesCache(queryClient, deviceId, payload);

    invalidateInterfaces();
  };

  // Обработчик статуса устройства (online/offline) - всегда активен на странице устройства
  disposers.push(
    router.on('device_status', (event: WebSocketEnvelope) => {
      const result = DeviceStatusSchema.safeParse(event.data ?? {});
      if (!result.success) {
        return;
      }

      const { status, device_id } = result.data;
      const id = Number(device_id);
      
      // Обновляем только если событие для текущего устройства
      if (!Number.isFinite(id) || id !== deviceId) {
        return;
      }

      // Обновляем статус устройства в кэше
      queryClient.setQueryData<Device | undefined>(['device', deviceId], (prev) => {
        if (!prev) {
          return prev;
        }
        
        const isOnline = status.toLowerCase() === 'online';
        return {
          ...prev,
          is_online: isOnline,
        };
      });
    }),
  );

  if (channelFlags.interfaces) {
    disposers.push(
      router.on('device_interfaces_update', (event) => interfaceHandler(event, 'realtime')),
    );
    disposers.push(
      router.on('device_interfaces_metrics', (event) => interfaceHandler(event, 'sync')),
    );
  }

  if (channelFlags.pon) {
    // ✅ ОПТИМИЗАЦИЯ: Используем refetchQueries вместо invalidateQueries для фонового обновления
    // Это предотвращает полный ререндер компонента, обновляя данные в фоне
    let ponRefetchTimer: number | null = null;
    const schedulePonRefetch = () => {
      if (ponRefetchTimer != null) return;
      ponRefetchTimer = window.setTimeout(() => {
        ponRefetchTimer = null;
        // Используем refetchQueries для фонового обновления без ререндера
        (queryClient as any).batch?.(() => {
          queryClient.refetchQueries({ 
            queryKey: ['device', deviceId, 'pon', 'metrics'],
            type: 'active', // Обновляем только активные queries
          });
          queryClient.refetchQueries({ 
            queryKey: ['device', deviceId, 'onu'],
            type: 'active', // Обновляем только активные queries
          });
        }) ?? ((
          queryClient.refetchQueries({ 
            queryKey: ['device', deviceId, 'pon', 'metrics'],
            type: 'active',
          }),
          queryClient.refetchQueries({ 
            queryKey: ['device', deviceId, 'onu'],
            type: 'active',
          })
        ));
      }, 300); // Увеличиваем дебаунс до 300ms для снижения нагрузки
    };

    disposers.push(
      router.on('pon_task_update', (event: WebSocketEnvelope) => {
        const parsed = PonTaskSchema.safeParse(event.data ?? {});
        if (!parsed.success) {
          schedulePonRefetch();
          return;
        }

        applyPonTaskUpdate(queryClient, parsed.data, deviceId);
        schedulePonRefetch();
      }),
    );

    const handleDeviceIdsPayload = (event: WebSocketEnvelope, _key: (string | number)[]) => {
      const parsed = DeviceIdOnlySchema.safeParse(event.data ?? {});
      if (!parsed.success) {
        schedulePonRefetch();
        return;
      }
      if (!matchesDevice(parsed.data.device_id)) {
        return;
      }
      schedulePonRefetch();
    };

    disposers.push(
      router.on('pon_onu_update', (event) =>
        handleDeviceIdsPayload(event, ['device', deviceId, 'onu']),
      ),
    );

    // Обработка батч-обновлений ONU (real-time обновления с батчингом)
    disposers.push(
      router.on('pon_onu_batch_update', (event: WebSocketEnvelope) => {
        const parsed = PonOnuBatchUpdateSchema.safeParse(event.data ?? {});
        if (!parsed.success) {
          schedulePonRefetch();
          return;
        }

        const { device_id } = parsed.data;
        const eventDeviceId = toNumber(device_id);
        
        // Проверяем, что событие относится к текущему устройству
        if (!matchesDevice(eventDeviceId)) {
          return;
        }

        // Обновляем данные ONU для устройства
        // Если onu_ids пустой или отсутствует - обновляем все ONU
        queryClient.invalidateQueries({ 
          queryKey: ['device', eventDeviceId, 'onu'],
          type: 'active',
        });
        
        // Также обновляем метрики PON
        queryClient.invalidateQueries({ 
          queryKey: ['device', eventDeviceId, 'pon', 'metrics'],
          type: 'active',
        });

        // Логируем для отладки
        if (parsed.data.count !== undefined && parsed.data.count > 0) {
          console.debug(`[PON] Batch update: device=${eventDeviceId}, count=${parsed.data.count}`);
        } else {
          console.debug(`[PON] Batch update: device=${eventDeviceId}, all ONU`);
        }
      }),
    );

    disposers.push(
      router.on('pon_statistics_update', (event) =>
        handleDeviceIdsPayload(event, ['device', deviceId, 'onu']),
      ),
    );
  }

  if (channelFlags.monitoring) {
    disposers.push(
      router.on('device_fdb_update', (event) => {
      const parsed = DeviceFdbSchema.safeParse(event.data ?? {});
      if (!parsed.success) {
        queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'fdb'] });
        return;
      }

      const payload = parsed.data;
      if (!matchesDevice(payload.device_id)) {
        return;
      }

      fdbBuffer.set(deviceId, {
        entries: Array.isArray(payload.entries) ? payload.entries : [],
        generatedAt:
          (typeof payload.generated_at === 'string' && payload.generated_at.length > 0
            ? payload.generated_at
            : undefined) ?? new Date().toISOString(),
      });

      scheduleFlush(`fdb:${deviceId}`, () => {
        const buffered = fdbBuffer.get(deviceId);
        if (!buffered) return;
        const worker = getRealtimeWorker();
        if (worker) {
          const handler = (ev: MessageEvent) => {
            worker.removeEventListener('message', handler as any);
            const data = ev.data as { ok: boolean; kind: string; items?: any[] };
            if (!data?.ok || data.kind !== 'fdb' || !Array.isArray(data.items)) {
              // fallback на пустой апдейт
              return;
            }
            queryClient.setQueryData(['device', deviceId, 'fdb'], data.items);
            fdbBuffer.delete(deviceId);
          };
          worker.addEventListener('message', handler as any, { once: true });
          worker.postMessage({ kind: 'fdb', entries: buffered.entries });
        } else {
          const entries = buffered.entries
            .map(normalizeFdbEntry)
            .filter((entry): entry is PollerFdbEntry => entry !== null);
          const normalizedEntries = entries.map((entry) => ({
            mac_address: entry.mac_address,
            vlan_id: entry.vlan_id ?? 0,
            port: entry.port ?? 0,
            port_name: resolvePortName(entry.port_name, entry.port),
            type: (entry.type ?? 'unknown').toLowerCase(),
            date_first: toIsoString(entry.date_first),
            date_last: toIsoString(entry.date_last) || buffered.generatedAt || new Date().toISOString(),
          }));
          queryClient.setQueryData(['device', deviceId, 'fdb'], normalizedEntries);
          fdbBuffer.delete(deviceId);
        }
      }, 50);
      }),
    );

    disposers.push(
      router.on('device_vlan_update', (event) => {
      const parsed = DeviceVlanSchema.safeParse(event.data ?? {});
      if (!parsed.success) {
        queryClient.invalidateQueries({ queryKey: ['devices', deviceId, 'poller', 'vlan'] });
        return;
      }

      const payload = parsed.data;
      if (!matchesDevice(payload.device_id)) {
        return;
      }

      const vlansRaw = Array.isArray(payload.vlans) ? payload.vlans : [];
      const vlans = vlansRaw
        .map(normalizeVlanEntry)
        .filter((entry): entry is PollerVlanEntry => entry !== null)
        .sort((a, b) => a.vlan_id - b.vlan_id);

      const generatedAt =
        (typeof payload.generated_at === 'string' && payload.generated_at.length > 0
          ? payload.generated_at
          : undefined) ?? new Date().toISOString();

      const taskNameCandidate = (() => {
        if (typeof payload.task === 'string') {
          const trimmed = payload.task.trim();
          if (trimmed.length > 0) {
            return trimmed;
          }
        }
        if (typeof payload.task_id === 'string') {
          const trimmed = payload.task_id.trim();
          if (trimmed.length > 0) {
            return trimmed;
          }
        }
        return undefined;
      })();

        queryClient.setQueryData<PollerVlanCacheResponse | undefined>(
          ['devices', deviceId, 'poller', 'vlan'],
          (prev) => {
            const previousPayload = prev?.data.payload ?? {};
            const nextPayload = {
              ...previousPayload,
              vlans,
            };

            return {
              data: {
                task: taskNameCandidate ?? prev?.data.task ?? 'VlanDatabase',
                payload: nextPayload,
              },
              meta: {
                generatedAt,
              },
            };
          },
        );
      }),
    );
  }

  return () => {
    // Очистка дебаунсеров и буферов
    for (const [, timer] of debounceTimers) {
      clearTimeout(timer);
    }
    debounceTimers.clear();
    fdbBuffer.clear();
    disposers.forEach((dispose) => dispose());
  };
};
